import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { User, Lock, Eye, EyeOff, Check, Shield } from "lucide-react";

function PasswordStrength({ password }) {
  const checks = [
    { label: "8+ characters", pass: password.length >= 8 },
    { label: "Uppercase letter", pass: /[A-Z]/.test(password) },
    { label: "Lowercase letter", pass: /[a-z]/.test(password) },
    { label: "Number", pass: /\d/.test(password) },
    { label: "Special character", pass: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];
  const score = checks.filter(c => c.pass).length;
  const colors = ["bg-red-500", "bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-400", "bg-green-600"];

  if (!password) return null;

  return (
    <div className="space-y-2 mt-2" data-testid="password-strength">
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < score ? colors[score] : "bg-muted"}`} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {checks.map(c => (
          <div key={c.label} className={`flex items-center gap-1.5 text-xs ${c.pass ? "text-green-600" : "text-muted-foreground"}`}>
            <Check className={`h-3 w-3 ${c.pass ? "opacity-100" : "opacity-30"}`} />
            {c.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, api, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [pwForm, setPwForm] = useState({ current: "", new: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    document.title = "Profile Settings | The Rig";
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || "", phone: user.phone || "" });
    }
  }, [user]);

  const saveProfile = async () => {
    if (!profileForm.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api("put", "/auth/profile", profileForm);
      toast.success("Profile updated");
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!pwForm.current) { toast.error("Enter your current password"); return; }
    if (pwForm.new.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pwForm.new !== pwForm.confirm) { toast.error("Passwords do not match"); return; }
    setChangingPw(true);
    try {
      await api("put", "/auth/change-password", {
        current_password: pwForm.current,
        new_password: pwForm.new,
      });
      toast.success("Password changed successfully");
      setPwForm({ current: "", new: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setChangingPw(false); }
  };

  if (authLoading || !user) return null;

  const roleColors = {
    admin: "bg-red-100 text-red-800",
    manager: "bg-blue-100 text-blue-800",
    member: "bg-green-100 text-green-800",
    guest: "bg-gray-100 text-gray-800",
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-testid="profile-page">
      <title>Profile Settings | The Rig</title>

      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-rig-charcoal">Profile Settings</h1>
        <p className="text-rig-charcoal/60 text-sm mt-1">Manage your account information and security</p>
      </div>

      {/* Account overview */}
      <Card className="mb-6 border-border rounded-none" data-testid="profile-overview-card">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-rig-teal text-white flex items-center justify-center text-xl font-bold flex-shrink-0">
              {user.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="font-heading font-bold text-lg text-rig-charcoal truncate">{user.name}</h2>
              <p className="text-sm text-muted-foreground truncate">{user.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`${roleColors[user.role] || roleColors.guest} border-0 text-xs`}>
                  {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                </Badge>
                {user.company_name && (
                  <span className="text-xs text-muted-foreground">{user.company_name}</span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile info */}
      <Card className="mb-6 border-border rounded-none" data-testid="profile-info-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-rig-teal" />
            <CardTitle className="text-base font-heading">Personal Information</CardTitle>
          </div>
          <CardDescription className="text-xs">Update your name and contact details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="profile-name">Full Name</Label>
            <Input
              id="profile-name"
              value={profileForm.name}
              onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <Label htmlFor="profile-email">Email</Label>
            <Input
              id="profile-email"
              value={user.email}
              disabled
              className="bg-muted cursor-not-allowed"
              data-testid="profile-email-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Email cannot be changed. Contact admin if needed.</p>
          </div>
          <div>
            <Label htmlFor="profile-phone">Phone</Label>
            <Input
              id="profile-phone"
              value={profileForm.phone}
              onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
              placeholder="e.g. +5999 123 4567"
              data-testid="profile-phone-input"
            />
          </div>
          <Button
            onClick={saveProfile}
            disabled={saving}
            className="bg-foreground text-background hover:bg-foreground/90 rounded-none"
            data-testid="profile-save-btn"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>

      <Separator className="my-6" />

      {/* Change password */}
      <Card className="border-border rounded-none" data-testid="password-change-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-rig-orange" />
            <CardTitle className="text-base font-heading">Change Password</CardTitle>
          </div>
          <CardDescription className="text-xs">Use a strong, unique password you don't use elsewhere</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="current-pw">Current Password</Label>
            <div className="relative">
              <Input
                id="current-pw"
                type={showCurrent ? "text" : "password"}
                value={pwForm.current}
                onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                placeholder="Enter current password"
                data-testid="current-password-input"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowCurrent(!showCurrent)}>
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="new-pw">New Password</Label>
            <div className="relative">
              <Input
                id="new-pw"
                type={showNew ? "text" : "password"}
                value={pwForm.new}
                onChange={e => setPwForm(p => ({ ...p, new: e.target.value }))}
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowNew(!showNew)}>
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <PasswordStrength password={pwForm.new} />
          </div>
          <div>
            <Label htmlFor="confirm-pw">Confirm New Password</Label>
            <Input
              id="confirm-pw"
              type="password"
              value={pwForm.confirm}
              onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
              placeholder="Re-enter new password"
              data-testid="confirm-password-input"
            />
            {pwForm.confirm && pwForm.new !== pwForm.confirm && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>
          <Button
            onClick={changePassword}
            disabled={changingPw || !pwForm.current || pwForm.new.length < 8 || pwForm.new !== pwForm.confirm}
            className="bg-foreground text-background hover:bg-foreground/90 rounded-none"
            data-testid="change-password-btn"
          >
            <Shield className="h-4 w-4 mr-2" />
            {changingPw ? "Changing..." : "Change Password"}
          </Button>
        </CardContent>
      </Card>

      <div className="mt-6 text-center">
        <p className="text-xs text-muted-foreground">
          You can also sign in using a <strong>one-time passcode</strong> sent to your email — no password needed.
        </p>
      </div>
    </div>
  );
}
